/**
 * CESM OSINT - Contexte Mode Démo
 * Permet de visualiser l'application sans pouvoir exécuter d'actions
 * Accès: 3 clics sur l'ancre de la page de connexion
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const DemoModeContext = createContext(null);

export const DemoModeProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoClickCount, setDemoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  // Gestion du triple-clic sur l'ancre
  const handleAnchorClick = useCallback(() => {
    const now = Date.now();
    
    // Reset si plus de 2 secondes entre les clics
    if (now - lastClickTime > 2000) {
      setDemoClickCount(1);
      setLastClickTime(now);
      return false;
    }
    
    const newCount = demoClickCount + 1;
    setDemoClickCount(newCount);
    setLastClickTime(now);
    
    if (newCount >= 3) {
      setIsDemoMode(true);
      setDemoClickCount(0);
      toast.info('🎭 MODE DÉMO ACTIVÉ', {
        description: 'Vous pouvez explorer l\'interface sans exécuter d\'actions.',
        duration: 5000
      });
      return true; // Mode démo activé
    }
    
    return false;
  }, [demoClickCount, lastClickTime]);

  // Fonction pour bloquer une action et afficher un message
  const blockAction = useCallback((actionName) => {
    if (!isDemoMode) return false;
    
    toast.warning(`🚫 Mode Démo`, {
      description: `Vous êtes en mode démo. Vous n'avez pas l'autorisation de lancer: ${actionName}`,
      duration: 3000
    });
    return true; // Action bloquée
  }, [isDemoMode]);

  // Vérifier si une action doit être bloquée (retourne true si bloqué)
  const checkDemoBlock = useCallback((actionName) => {
    return blockAction(actionName);
  }, [blockAction]);

  // Quitter le mode démo
  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    toast.success('Mode démo désactivé', {
      description: 'Connectez-vous pour accéder à toutes les fonctionnalités.',
      duration: 3000
    });
  }, []);

  const value = {
    isDemoMode,
    setIsDemoMode,
    handleAnchorClick,
    checkDemoBlock,
    blockAction,
    exitDemoMode,
    demoClickCount
  };

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = () => {
  const context = useContext(DemoModeContext);
  if (!context) {
    // Retourner des valeurs par défaut si pas de provider
    return {
      isDemoMode: false,
      handleAnchorClick: () => false,
      checkDemoBlock: () => false,
      blockAction: () => false,
      exitDemoMode: () => {},
      demoClickCount: 0
    };
  }
  return context;
};

export default DemoModeContext;
